// @oasis-permission-inherits: /basecamp/evals (evals:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { BasecampEvalListing, type EvalStyleFilter } from "~/modules/basecamp-evals/eval-listing";
import { uniqueEvalComparisons } from "~/modules/basecamp-evals/model";
import { listBasecampEvalsInfiniteQuery } from "~/modules/basecamp-evals/queries";
import { usePageTitle } from "~/modules/page-title/page-title";

export const Route = createFileRoute("/_dash/basecamp/evals/")({ component: BasecampEvalsPage });

function BasecampEvalsPage() {
  usePageTitle("Basecamp Evals");
  const [style, setStyle] = useState<EvalStyleFilter>("all");
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = listBasecampEvalsInfiniteQuery.useInfiniteQuery(
    { params: { style: style === "all" ? undefined : style } },
    { refetchInterval: 30_000 }
  );
  const evals = uniqueEvalComparisons(data?.pages ?? []);
  return (
    <BasecampEvalListing
      evals={evals}
      style={style}
      onStyleChange={setStyle}
      refreshControl={
        <Button variant="outline" size="sm" disabled={isFetching} onClick={() => void refetch()}>
          Refresh
        </Button>
      }
      feedback={
        <>
          {error && (
            <p
              role="alert"
              className="rounded-lg border border-destructive p-4 text-sm text-destructive"
            >
              Could not load Basecamp evals: {error.message}
            </p>
          )}
          {isLoading && <Skeleton className="h-64 w-full" />}
          {!isLoading && !error && evals.length === 0 && (
            <p className="rounded-lg border p-10 text-center text-muted-foreground">
              No evals match this style. Apply a Basecamp eval label to a PR to publish a
              comparison.
            </p>
          )}
        </>
      }
      footer={
        <>
          {hasNextPage && (
            <Button
              variant="outline"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          )}
          {style === "all" && hasNextPage && (
            <p className="text-xs text-muted-foreground">Choose a style to browse older evals.</p>
          )}
        </>
      }
      renderLink={(item, children, className) => (
        <Link
          to="/basecamp/evals/$comparisonId"
          params={{ comparisonId: item.comparisonId }}
          className={className}
        >
          {children}
        </Link>
      )}
    />
  );
}
